import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { toast } from "../stores/toast";
import { PageHeader } from "../components/storefront/PageHeader";
import { Button, Input, PageLoader } from "../components/ui";
import { CountrySelect } from "../components/CountrySelect";
import { PhoneInput } from "../components/PhoneInput";
import { AddressRegionField, formatAddressLocation } from "../components/AddressRegionField";
import { emptyAddressForm, patchAddressForm, type AddressFormFields } from "../lib/addressForm";

interface AddressDto {
  id: string;
  label: string;
  name: string;
  phone: string;
  address1: string;
  address2: string | null;
  city: string;
  state: string;
  postcode: string;
  country: string;
  isDefault: boolean;
}

const emptyForm: AddressFormFields = emptyAddressForm("", false);

export function AccountAddresses() {
  const queryClient = useQueryClient();
  const [formTarget, setFormTarget] = useState<{ kind: "add" } | { kind: "edit"; id: string } | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["addresses"],
    queryFn: () => api<{ items: AddressDto[] }>("/addresses"),
  });

  function startAdd() {
    setForm(emptyForm);
    setFormTarget({ kind: "add" });
  }

  function startEdit(addr: AddressDto) {
    setForm({
      label: addr.label,
      name: addr.name,
      phone: addr.phone,
      address1: addr.address1,
      address2: addr.address2 ?? "",
      postcode: addr.postcode,
      city: addr.city,
      state: addr.state,
      country: addr.country,
      isDefault: addr.isDefault,
    });
    setFormTarget({ kind: "edit", id: addr.id });
  }

  function cancelForm() {
    setForm(emptyForm);
    setFormTarget(null);
  }

  async function saveAddress(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address1 || !form.postcode || !form.city || !form.country || !form.state) {
      toast("error", "Fill in all required fields");
      return;
    }
    setBusy(true);
    try {
      const body = {
        ...form,
        address2: form.address2 || null,
      };
      if (formTarget?.kind === "edit") {
        await api(`/addresses/${formTarget.id}`, { method: "PUT", body });
        toast("success", "Address updated");
      } else {
        await api("/addresses", { body });
        toast("success", "Address saved");
      }
      await queryClient.invalidateQueries({ queryKey: ["addresses"] });
      cancelForm();
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't save address");
    } finally {
      setBusy(false);
    }
  }

  async function deleteAddress(id: string) {
    try {
      await api(`/addresses/${id}`, { method: "DELETE" });
      await queryClient.invalidateQueries({ queryKey: ["addresses"] });
      toast("success", "Address deleted");
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't delete");
    }
  }

  async function setDefault(id: string) {
    setBusy(true);
    try {
      await api(`/addresses/${id}`, { method: "PUT", body: { isDefault: true } });
      await queryClient.invalidateQueries({ queryKey: ["addresses"] });
      toast("success", "Default address updated");
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't update");
    } finally {
      setBusy(false);
    }
  }

  if (isLoading) return <PageLoader />;

  return (
    <div className="space-y-4 px-4 pt-4 pb-8 lg:px-6">
      <Link to="/account" className="text-xs font-medium text-brand-pink">
        ← Account
      </Link>
      <PageHeader
        title="Addresses"
        description="Save shipping addresses for faster checkout and vault shipments."
      />

      {!data?.items.length && !formTarget && (
        <div className="glass-panel rounded-2xl p-6 text-center">
          <MapPin size={32} className="mx-auto text-white/30" />
          <p className="mt-3 text-sm text-white/60">No saved addresses yet</p>
          <Button className="mt-4" onClick={startAdd}>
            Add address
          </Button>
        </div>
      )}

      {data?.items.map((addr) => (
        <div key={addr.id} className="glass-panel rounded-2xl p-4 text-xs">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-semibold text-white/90">
                {addr.label}
                {addr.isDefault && (
                  <span className="ml-1.5 rounded-full bg-brand-pink/20 px-1.5 py-0.5 text-[9px] text-brand-pink">
                    Default
                  </span>
                )}
              </p>
              <p className="mt-1 font-medium">{addr.name}</p>
              <p className="mt-0.5 text-white/60">
                {addr.address1}
                {addr.address2 ? `, ${addr.address2}` : ""},{" "}
                {formatAddressLocation(addr)}
              </p>
              <p className="text-white/45">{addr.phone}</p>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              variant="outline"
              disabled={busy || formTarget?.kind === "edit" && formTarget.id === addr.id}
              onClick={() => startEdit(addr)}
            >
              Edit
            </Button>
            {!addr.isDefault && (
              <Button variant="outline" disabled={busy} onClick={() => void setDefault(addr.id)}>
                Set default
              </Button>
            )}
            <Button variant="ghost" disabled={busy} onClick={() => void deleteAddress(addr.id)}>
              Delete
            </Button>
          </div>
        </div>
      ))}

      {data?.items.length && !formTarget ? (
        <Button variant="outline" className="w-full" onClick={startAdd}>
          Add address
        </Button>
      ) : null}

      {formTarget && (
        <form onSubmit={saveAddress} className="glass-panel sidebar-form space-y-0 rounded-2xl p-4">
          <p className="mb-3 font-display text-xs font-semibold uppercase tracking-widest text-white/40">
            {formTarget.kind === "edit" ? "Edit address" : "New address"}
          </p>
          <Input
            label="Label"
            placeholder="Home"
            value={form.label}
            onChange={(e) => patchAddressForm(setForm, { label: e.target.value })}
          />
          <Input
            label="Recipient name"
            placeholder="Full name"
            value={form.name}
            onChange={(e) => patchAddressForm(setForm, { name: e.target.value })}
          />
          <PhoneInput
            label="Phone"
            placeholder="Mobile number"
            value={form.phone}
            onChange={(phone) => patchAddressForm(setForm, { phone })}
            country={form.country}
          />
          <Input
            label="Address line 1"
            placeholder="Street address"
            value={form.address1}
            onChange={(e) => patchAddressForm(setForm, { address1: e.target.value })}
          />
          <Input
            label="Address line 2"
            placeholder="Unit, floor (optional)"
            value={form.address2}
            onChange={(e) => patchAddressForm(setForm, { address2: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Postcode"
              inputMode="numeric"
              placeholder="Postcode"
              value={form.postcode}
              onChange={(e) => patchAddressForm(setForm, { postcode: e.target.value })}
            />
            <Input
              label="City"
              placeholder="City"
              value={form.city}
              onChange={(e) => patchAddressForm(setForm, { city: e.target.value })}
            />
          </div>
          <CountrySelect
            label="Country"
            required
            value={form.country}
            onChange={(country) =>
              patchAddressForm(setForm, (prev) => ({
                country,
                state: country === prev.country ? prev.state : "",
              }))
            }
          />
          <AddressRegionField
            country={form.country}
            state={form.state}
            onStateChange={(state) => patchAddressForm(setForm, { state })}
          />
          <label className="flex items-center gap-2 text-xs text-white/70">
            <input
              type="checkbox"
              checked={form.isDefault}
              onChange={(e) => patchAddressForm(setForm, { isDefault: e.target.checked })}
            />
            Set as default
          </label>
          <div className="form-actions-footer flex gap-2 pt-2">
            <Button type="button" variant="outline" className="flex-1" onClick={cancelForm}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={busy}>
              Save
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
